/* ============================================
   TFS Wheels — Global JS
   PubSub, Cart API, Utilities
   ============================================ */

/* --- PubSub Event System --- */
const PubSub = (() => {
  const events = {};
  return {
    subscribe(event, callback) {
      events[event] = events[event] || [];
      events[event].push(callback);
      return () => {
        events[event] = events[event].filter(cb => cb !== callback);
      };
    },
    publish(event, data) {
      if (events[event]) {
        events[event].forEach(callback => callback(data));
      }
    }
  };
})();

/* --- Cart API Helpers --- */
const CartAPI = {
  async get() {
    const res = await fetch(window.routes.cart_url + '.js');
    return res.json();
  },

  async add(items) {
    const res = await fetch(window.routes.cart_add_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || data.message);
    PubSub.publish('cart:updated');
    return data;
  },

  async change(body) {
    const res = await fetch(window.routes.cart_change_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || data.message);
    PubSub.publish('cart:updated');
    return data;
  },

  async update(body) {
    const res = await fetch(window.routes.cart_update_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || data.message);
    PubSub.publish('cart:updated');
    return data;
  }
};

/* --- Debounce --- */
function debounce(fn, delay = 300) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
}

/* --- Trap Focus --- */
function trapFocus(container, elementToFocus = container) {
  const focusable = container.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );
  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  function handleKeydown(e) {
    if (e.key !== 'Tab') return;
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  container.addEventListener('keydown', handleKeydown);
  if (elementToFocus) elementToFocus.focus();

  return () => container.removeEventListener('keydown', handleKeydown);
}

/* --- Remove Trap Focus --- */
function removeTrapFocus(container) {
  // Focus returns to previously focused element
  if (container && container._previousFocus) {
    container._previousFocus.focus();
  }
}

/* --- Format Money --- */
function formatMoney(cents, format) {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  const value = (cents / 100).toFixed(2);
  const moneyFormat = format || window.Shopify?.money_format || '${{amount}}';
  return moneyFormat
    .replace('{{amount}}', value)
    .replace('{{amount_no_decimals}}', Math.round(cents / 100))
    .replace('{{amount_with_comma_separator}}', value.replace('.', ','));
}

/* --- Fetch Section HTML --- */
async function fetchSection(sectionId, url) {
  const fetchUrl = url || window.location.pathname;
  const separator = fetchUrl.includes('?') ? '&' : '?';
  const res = await fetch(`${fetchUrl}${separator}sections=${sectionId}`);
  const data = await res.json();
  return data[sectionId];
}

/* --- Update Cart Count Badge --- */
function updateCartCount(count) {
  document.querySelectorAll('.cart-count-badge').forEach(el => {
    el.textContent = count;
    el.toggleAttribute('hidden', count === 0);
  });
}

// Listen for cart updates to refresh count
PubSub.subscribe('cart:updated', async () => {
  try {
    const cart = await CartAPI.get();
    updateCartCount(cart.item_count);
  } catch (e) {
    // Silently fail
  }
});

/* --- Quantity Input Web Component --- */
if (!customElements.get('quantity-input')) {
  class QuantityInput extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input');
      this.minus = this.querySelector('[name="minus"]');
      this.plus = this.querySelector('[name="plus"]');
    }

    connectedCallback() {
      this.minus?.addEventListener('click', this.decrement.bind(this));
      this.plus?.addEventListener('click', this.increment.bind(this));
      this.input?.addEventListener('change', this.validate.bind(this));
    }

    decrement() {
      const min = parseInt(this.input.min) || 1;
      const value = parseInt(this.input.value) - 1;
      this.input.value = Math.max(min, value);
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    increment() {
      const max = parseInt(this.input.max) || 999;
      const value = parseInt(this.input.value) + 1;
      this.input.value = Math.min(max, value);
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    validate() {
      const min = parseInt(this.input.min) || 1;
      const max = parseInt(this.input.max) || 999;
      let value = parseInt(this.input.value);
      if (isNaN(value) || value < min) value = min;
      if (value > max) value = max;
      this.input.value = value;
    }
  }

  customElements.define('quantity-input', QuantityInput);
}
